import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { 
  Play, 
  Plus, 
  Video, 
  BarChart3, 
  BookOpen, 
  Clock,
  Eye,
  Star,
  Award
} from 'lucide-react';

interface PracticeVideo {
  id: string;
  title: string;
  description: string;
  video_url: string;
  status: string;
  created_at: string;
  feedback_count?: number;
  courses?: { title: string } | null;
}

interface VideoCollection {
  id: string;
  name: string;
  description: string;
  practice_video_ids: string[];
  created_at: string;
  practice_videos?: PracticeVideo[];
}

export default function CoachingVideoPortfolioSample() {
  const { toast } = useToast();
  const [practiceVideos, setPracticeVideos] = useState<PracticeVideo[]>([]);
  const [collections, setCollections] = useState<VideoCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newCollection, setNewCollection] = useState({
    name: '',
    description: '',
    practice_video_ids: [] as string[]
  });

  useEffect(() => {
    loadPracticeVideos();
    loadCollections();
  }, []);

  const loadPracticeVideos = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('practice_videos')
        .select(`
          id,
          title,
          description,
          video_url,
          status,
          created_at,
          courses!left (title),
          coaching_feedback!left (id)
        `)
        .eq('user_id', user.id)
        .eq('status', 'analyzed')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const videosWithFeedback = data?.map(video => ({
        ...video,
        feedback_count: video.coaching_feedback?.length || 0
      })) || [];

      setPracticeVideos(videosWithFeedback);
    } catch (error) {
      console.error('Error loading practice videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCollections = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // For this sample, we'll create mock collections
      const mockCollections = [
        {
          id: '1',
          name: 'My Best Teaching Moments',
          description: 'Videos showcasing effective instructional strategies with AI coaching insights',
          practice_video_ids: [],
          created_at: new Date().toISOString(),
          practice_videos: []
        },
        {
          id: '2', 
          name: 'Professional Growth Journey',
          description: 'Documenting my improvement over time with AI feedback',
          practice_video_ids: [],
          created_at: new Date().toISOString(),
          practice_videos: []
        }
      ];

      setCollections(mockCollections);
    } catch (error) {
      console.error('Error loading collections:', error);
    }
  };

  const handleCreateCollection = async () => {
    if (!newCollection.name.trim()) {
      toast({
        title: "Error",
        description: "Please enter a collection name",
        variant: "destructive"
      });
      return;
    }

    // Simulate collection creation
    const newCol: VideoCollection = {
      id: Date.now().toString(),
      name: newCollection.name,
      description: newCollection.description,
      practice_video_ids: newCollection.practice_video_ids,
      created_at: new Date().toISOString(),
      practice_videos: practiceVideos.filter(v => newCollection.practice_video_ids.includes(v.id))
    };

    setCollections(prev => [newCol, ...prev]);
    setShowCreateDialog(false);
    setNewCollection({ name: '', description: '', practice_video_ids: [] });

    toast({
      title: "Success",
      description: "Video collection created successfully"
    });
  };

  const toggleVideoSelection = (videoId: string) => {
    setNewCollection(prev => ({
      ...prev,
      practice_video_ids: prev.practice_video_ids.includes(videoId)
        ? prev.practice_video_ids.filter(id => id !== videoId)
        : [...prev.practice_video_ids, videoId]
    }));
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-64 bg-muted rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">AI Coaching Video Collections</h2>
          <p className="text-muted-foreground">
            Organize your practice videos with AI coaching feedback for portfolio sharing
          </p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Collection
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Video Collection</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Collection Name</Label>
                <Input
                  id="name"
                  value={newCollection.name}
                  onChange={(e) => setNewCollection(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., My Best Teaching Strategies"
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={newCollection.description}
                  onChange={(e) => setNewCollection(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe what this collection showcases..."
                  rows={3}
                />
              </div>
              <div>
                <Label>Select Practice Videos</Label>
                <div className="max-h-64 overflow-y-auto space-y-2 border rounded-lg p-3">
                  {practiceVideos.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No analyzed practice videos available yet.
                      Upload and analyze videos from the AI Coaching section first.
                    </p>
                  ) : (
                    practiceVideos.map(video => (
                      <div key={video.id} className="flex items-start space-x-3 p-2 rounded border">
                        <Checkbox
                          checked={newCollection.practice_video_ids.includes(video.id)}
                          onCheckedChange={() => toggleVideoSelection(video.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-medium truncate">{video.title}</h4>
                            <div className="flex items-center gap-2 ml-2">
                              <Badge variant="outline" className="text-xs">
                                {video.feedback_count || 0} insights
                              </Badge>
                              <Badge className="text-xs bg-green-100 text-green-800">
                                {video.status}
                              </Badge>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {video.description}
                          </p>
                          {video.courses && (
                            <p className="text-xs text-blue-600 mt-1">
                              Related: {video.courses.title}
                            </p>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateCollection}>
                  Create Collection
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {collections.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Video className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Collections Yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first collection to showcase your teaching practice with AI coaching insights
            </p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Collection
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {collections.map(collection => (
            <Card key={collection.id} className="overflow-hidden">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {collection.name}
                      <Badge variant="outline" className="ml-2">
                        {collection.practice_video_ids.length} videos
                      </Badge>
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {collection.description}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                      <BarChart3 className="h-3 w-3 mr-1" />
                      AI Insights
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {collection.practice_videos && collection.practice_videos.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {collection.practice_videos.map(video => (
                      <Card key={video.id} className="border-muted hover:shadow-md transition-shadow">
                        <CardContent className="p-0">
                          <div className="aspect-video bg-muted relative overflow-hidden">
                            <video
                              src={video.video_url}
                              className="w-full h-full object-cover"
                              preload="metadata"
                            />
                            <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                              <Button size="sm" variant="secondary">
                                <Play className="h-3 w-3 mr-1" />
                                Watch
                              </Button>
                            </div>
                            <div className="absolute top-2 right-2">
                              <Badge className="bg-white/90 text-gray-800">
                                <Star className="h-3 w-3 mr-1" />
                                AI Analyzed
                              </Badge>
                            </div>
                          </div>
                          <div className="p-4">
                            <h4 className="font-medium text-sm line-clamp-2 mb-2">
                              {video.title}
                            </h4>
                            <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
                              {video.description}
                            </p>
                            <div className="flex items-center justify-between text-xs">
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <Clock className="h-3 w-3" />
                                {new Date(video.created_at).toLocaleDateString()}
                              </div>
                              <div className="flex items-center gap-1 text-blue-600">
                                <BookOpen className="h-3 w-3" />
                                {video.feedback_count || 0} insights
                              </div>
                            </div>
                            {video.courses && (
                              <div className="mt-2">
                                <Badge variant="outline" className="text-xs">
                                  {video.courses.title}
                                </Badge>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Video className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">
                      No videos in this collection yet. Add some from your analyzed practice videos.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Sample Benefits Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5 text-blue-600" />
            Portfolio Benefits with AI Coaching Videos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <h4 className="font-medium">Professional Growth Documentation</h4>
              <ul className="space-y-1 text-muted-foreground">
                <li>• Showcase evidence-based teaching improvements</li>
                <li>• AI insights validate effective practices</li>
                <li>• Track development over time with feedback</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium">Enhanced Portfolio Value</h4>
              <ul className="space-y-1 text-muted-foreground">
                <li>• Research-backed coaching annotations</li>
                <li>• Objective analysis of teaching strategies</li>
                <li>• Professional reflection with AI guidance</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}